/**
 * Custom error class for service layer errors with consistent error handling
 */

export type ErrorCode =
  | 'AUTHENTICATION_ERROR'
  | 'PERMISSION_DENIED'
  | 'NOT_FOUND'
  | 'VALIDATION_ERROR'
  | 'NETWORK_ERROR'
  | 'TIMEOUT_ERROR'
  | 'INTERNAL_ERROR'
  | 'CONFLICT_ERROR'
  | 'RATE_LIMIT_ERROR'
  | 'FILE_TOO_LARGE'
  | 'INVALID_FILE_TYPE'
  | 'STORAGE_ERROR'
  | 'STORAGE_NOT_INITIALIZED'
  | 'STORAGE_UNAUTHORIZED';

export interface ServiceErrorContext {
  code?: ErrorCode;
  statusCode?: number;
  operation?: string;
  details?: Record<string, any>;
  originalError?: Error | unknown;
}

export class ServiceError extends Error {
  public readonly code: ErrorCode;
  public readonly statusCode: number;
  public readonly operation?: string;
  public readonly details?: Record<string, any>;
  public readonly originalError?: Error | unknown;
  public readonly timestamp: Date;

  constructor(message: string, context?: ServiceErrorContext) {
    super(message);
    this.name = 'ServiceError';
    this.code = context?.code || 'INTERNAL_ERROR';
    this.statusCode = context?.statusCode || 500;
    this.operation = context?.operation;
    this.details = context?.details;
    this.originalError = context?.originalError;
    this.timestamp = new Date();

    // Maintains proper stack trace for where our error was thrown
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ServiceError);
    }
  }

  /**
   * Get user-friendly error message
   */
  getUserMessage(): string {
    switch (this.code) {
      case 'AUTHENTICATION_ERROR':
        return 'Please sign in to continue.';
      case 'PERMISSION_DENIED':
        return 'You do not have permission to perform this action.';
      case 'NOT_FOUND':
        return 'The requested resource was not found.';
      case 'VALIDATION_ERROR':
        return this.message || 'Please check your input and try again.';
      case 'NETWORK_ERROR':
        return 'Network error. Please check your connection and try again.';
      case 'TIMEOUT_ERROR':
        return 'The request timed out. Please try again.';
      case 'CONFLICT_ERROR':
        return 'A conflict occurred. The resource may have been modified.';
      case 'RATE_LIMIT_ERROR':
        return 'Too many requests. Please wait a moment and try again.';
      default:
        return this.message || 'An unexpected error occurred. Please try again.';
    }
  }

  /**
   * Check if error is retryable
   */
  isRetryable(): boolean {
    return ['NETWORK_ERROR', 'TIMEOUT_ERROR', 'RATE_LIMIT_ERROR'].includes(this.code);
  }

  /**
   * Convert to JSON for logging
   */
  toJSON() {
    return {
      name: this.name,
      message: this.message,
      code: this.code,
      statusCode: this.statusCode,
      operation: this.operation,
      details: this.details,
      timestamp: this.timestamp.toISOString(),
      stack: this.stack,
    };
  }

  /**
   * Parse Firebase error into ServiceError
   */
  static fromFirebaseError(error: any, operation?: string): ServiceError {
    const message = error.message || 'An error occurred';

    let code: ErrorCode = 'INTERNAL_ERROR';
    let statusCode = 500;

    if (message.includes('unauthenticated') || message.includes('auth/')) {
      code = 'AUTHENTICATION_ERROR';
      statusCode = 401;
    } else if (message.includes('permission-denied')) {
      code = 'PERMISSION_DENIED';
      statusCode = 403;
    } else if (message.includes('not-found')) {
      code = 'NOT_FOUND';
      statusCode = 404;
    } else if (message.includes('invalid-argument') || message.includes('validation')) {
      code = 'VALIDATION_ERROR';
      statusCode = 400;
    } else if (message.includes('already-exists')) {
      code = 'CONFLICT_ERROR';
      statusCode = 409;
    } else if (message.includes('network') || message.includes('unavailable')) {
      code = 'NETWORK_ERROR';
      statusCode = 503;
    } else if (message.includes('timeout') || message.includes('deadline-exceeded')) {
      code = 'TIMEOUT_ERROR';
      statusCode = 504;
    } else if (message.includes('resource-exhausted')) {
      code = 'RATE_LIMIT_ERROR';
      statusCode = 429;
    }

    return new ServiceError(message, {
      code,
      statusCode,
      operation,
      originalError: error,
    });
  }

  /**
   * Parse generic error into ServiceError
   */
  static fromError(error: unknown, operation?: string): ServiceError {
    if (error instanceof ServiceError) {
      return error;
    }

    if (error instanceof Error) {
      return new ServiceError(error.message, {
        code: 'INTERNAL_ERROR',
        operation,
        originalError: error,
      });
    }

    return new ServiceError('An unknown error occurred', {
      code: 'INTERNAL_ERROR',
      operation,
      originalError: error,
    });
  }
}

/**
 * Helper function to handle service errors consistently
 */
export function handleServiceError(error: unknown, operation: string): never {
  console.error(`Service error in ${operation}:`, error);

  if (error instanceof ServiceError) {
    throw error;
  }

  const serviceError = ServiceError.fromFirebaseError(error, operation);
  throw serviceError;
}